/**
 * Modal proprio do LFRCORD antes de compartilhar a tela. Escolhe qualidade,
 * conteudo e se o audio do computador entra na transmissao — e so ENTAO abre
 * o seletor nativo do navegador para escolher qual tela/janela/aba.
 *
 * No navegador, o seletor de tela em si (qual monitor, janela ou aba) e do
 * sistema operacional: nenhum site consegue substituir essa etapa por uma UI
 * propria, e por design. No app desktop (Electron), o LFRCORD intercepta esse
 * pedido e mostra o proprio seletor — ver `electron/main.ts`.
 */
import { useEffect, type CSSProperties, type ReactElement } from "react";
import { QUALITY_ORDER, QUALITY_PRESETS, type ContentHintMode, type QualityId } from "@shared/protocol";
import { C, FONT_MONO, hexA } from "@/theme";
import { useApp } from "@/state/store";
import { CloseIcon, ScreenIcon } from "@/components/icons";

const MONO_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const CONTENT_MODES: { id: ContentHintMode; label: string; hint: string }[] = [
  { id: "motion", label: "Movimento", hint: "Prioriza quadros por segundo — jogos e vídeo." },
  { id: "detail", label: "Nitidez", hint: "Prioriza nitidez — texto e código." },
];

function AudioIcon({ size = 16 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 14a8 8 0 0 1 16 0" />
      <rect x="3" y="14" width="4" height="6" rx="1.5" />
      <rect x="17" y="14" width="4" height="6" rx="1.5" />
    </svg>
  );
}

/** Chave de 40x22, igual ao toggle das configurações. */
function Toggle({ on, onToggle, title }: { on: boolean; onToggle: () => void; title: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      title={title}
      onClick={onToggle}
      style={{
        width: 40,
        height: 22,
        flex: "0 0 auto",
        position: "relative",
        padding: 0,
        border: 0,
        borderRadius: 999,
        cursor: "pointer",
        background: on ? "var(--ac, #1F4FD8)" : C.track,
        transition: "background 150ms ease",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          width: 16,
          height: 16,
          borderRadius: 999,
          background: C.text,
          transition: "left 150ms ease",
          left: on ? 21 : 3,
        }}
      />
    </button>
  );
}

export function ShareScreenModal(): ReactElement | null {
  const { state, actions } = useApp();
  const open = state.shareModalOpen;
  const isElectron = Boolean(window.lfrcordDesktop);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") actions.closeShareModal();
      if (event.key === "Enter") actions.confirmShare();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [actions, open]);

  if (!open) return null;

  const preset = QUALITY_PRESETS[state.shareQuality];

  return (
    <div
      onClick={actions.closeShareModal}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: 420,
          maxWidth: "100%",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          background: C.stage,
          animation: "pop 200ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "18px 20px 0",
          }}
        >
          <span
            style={{
              width: 34,
              height: 34,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              borderRadius: 10,
              background: hexA(C.accent, 0.16),
              color: C.accent,
            }}
          >
            <ScreenIcon size={17} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Transmitir sua tela</div>
          </div>
          <button
            type="button"
            onClick={actions.closeShareModal}
            title="Fechar"
            className="hov-bg-26-f2"
            style={{
              width: 30,
              height: 30,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              padding: 0,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 999,
              cursor: "pointer",
              background: "transparent",
              color: C.textMuted,
            }}
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div style={{ display: "grid", gap: 16, padding: "16px 20px 20px" }}>
          <label style={{ display: "grid", gap: 7 }}>
            <span style={MONO_LABEL}>QUALIDADE</span>
            <select
              value={state.shareQuality}
              onChange={event => {
                const next = QUALITY_ORDER.find(id => id === (event.target.value as QualityId));
                if (next) actions.setShareQuality(next);
              }}
              style={{
                padding: "10px 12px",
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 9,
                background: C.field,
                color: C.text,
                fontSize: 14,
                outline: 0,
                cursor: "pointer",
              }}
            >
              {QUALITY_ORDER.map(id => (
                <option key={id} value={id}>
                  {QUALITY_PRESETS[id].label}
                </option>
              ))}
            </select>
            {preset.height !== null && preset.frameRate !== null && (
              <span style={{ fontSize: 11.5, color: C.textDim }}>
                Pede {preset.width}×{preset.height} a {preset.frameRate} fps para a fonte
                capturada. Se ela não aceitar, o LFRCORD avisa qual qualidade saiu de verdade.
              </span>
            )}
          </label>

          <div>
            <div style={{ ...MONO_LABEL, marginBottom: 8 }}>CONTEÚDO</div>
            <div style={{ display: "flex", gap: 8 }}>
              {CONTENT_MODES.map(mode => {
                const active = state.contentHint === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    title={mode.hint}
                    onClick={() => actions.setContentHint(mode.id)}
                    style={{
                      flex: 1,
                      padding: "9px 0",
                      border: 0,
                      borderRadius: 9,
                      cursor: "pointer",
                      background: active ? hexA(C.accent, 0.2) : C.field,
                      color: active ? C.accent : C.textMuted,
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "12px 14px",
              border: `1px solid ${C.lineSoft}`,
              borderRadius: 10,
              background: C.field,
            }}
          >
            <span style={{ color: C.textMuted, marginTop: 1 }}>
              <AudioIcon size={16} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>
                  Incluir áudio do computador
                </span>
                <Toggle
                  on={state.shareSystemAudio}
                  onToggle={() => actions.setShareSystemAudio(!state.shareSystemAudio)}
                  title="Incluir áudio do computador na transmissão"
                />
              </div>
              <div style={{ marginTop: 4, fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>
                {state.shareSystemAudio
                  ? isElectron
                    ? "Isso inclui qualquer som que o seu computador reproduzir. O app desktop captura esse áudio excluindo o próprio LFRCORD — a voz da call não volta na transmissão."
                    : "Isso inclui qualquer som que o seu computador reproduzir. O navegador não consegue excluir a call — então, enquanto você transmitir assim, o som dos outros membros fica mudo só pra você (o seu microfone continua normal pra todo mundo)."
                  : "Desligado: só o vídeo da tela vai na transmissão. O som do LFRCORD nunca sai — o microfone continua funcionando normalmente."}
              </div>
            </div>
          </div>

          <div
            style={{
              fontSize: 11.5,
              color: C.textGhost,
              lineHeight: 1.5,
            }}
          >
            {isElectron
              ? "Depois de continuar, o LFRCORD mostra o seletor próprio de tela, janela ou aba."
              : "Depois de continuar, o navegador pergunta qual tela, janela ou aba compartilhar — essa etapa é do sistema, nenhum site pode substituí-la."}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={actions.confirmShare}
              className="hov-bright-108"
              style={{
                flex: 1,
                padding: "11px 0",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: "var(--ac, #1F4FD8)",
                color: C.ink,
                fontSize: 13.5,
                fontWeight: 700,
              }}
            >
              Continuar
            </button>
            <button
              type="button"
              onClick={actions.closeShareModal}
              className="hov-bg-26"
              style={{
                padding: "11px 16px",
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 10,
                cursor: "pointer",
                background: "transparent",
                color: C.textMuted,
                fontSize: 13.5,
                fontWeight: 600,
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
